/* Section Rendering API returns section.index = nil, so the response contains
   real content instead of another placeholder. One registration across instances. */
if (!customElements.get('ebc-deferred')) {
  const assets = new Map();
  const pending = [];
  let active = 0;
  const schedule = (task) => new Promise((resolve, reject) => {
    pending.push({ task, resolve, reject });
    drain();
  });
  function drain() {
    while (active < 2 && pending.length) {
      const { task, resolve, reject } = pending.shift();
      active++;
      task().then(resolve, reject).finally(() => { active--; drain(); });
    }
  }
  function loadAsset(node) {
    const url = node.src || node.href;
    if (assets.has(url)) return assets.get(url);
    const selector = node.tagName === 'SCRIPT' ? 'script[src]' : 'link[rel="stylesheet"]';
    if ([...document.querySelectorAll(selector)].some(existing => (existing.src || existing.href) === url)) return Promise.resolve();
    const promise = new Promise((resolve, reject) => {
      const copy = document.createElement(node.tagName.toLowerCase());
      for (const attribute of node.attributes) copy.setAttribute(attribute.name, attribute.value);
      const timeout = setTimeout(() => fail(), 15000);
      function fail() {
        clearTimeout(timeout);
        copy.remove();
        assets.delete(url);
        reject(new Error('EBC asset failed'));
      }
      copy.onload = () => { clearTimeout(timeout); resolve(); };
      copy.onerror = fail;
      document.head.append(copy);
    });
    assets.set(url, promise);
    return promise;
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      observer.unobserve(entry.target);
      entry.target.load();
    }
  }, { rootMargin: '600px 0px' }) : null;
  const loadDependencies = nodes => Promise.all((nodes || []).map(loadAsset));
  class EbcDeferred extends HTMLElement {
    connectedCallback() {
      if (this.loading) return;
      if (observer) observer.observe(this);
      else this.load();
    }
    disconnectedCallback() { observer?.unobserve(this); }
    async load() {
      if (this.loading || !this.isConnected) return;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      try {
        if (this.fragment) {
          await loadDependencies(this.dependencies);
          if (!this.isConnected) return;
          const fragment = this.fragment;
          this.fragment = null;
          this.dependencies = null;
          this.replaceWith(fragment);
          return;
        }
        const url = new URL(window.location.href);
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
        const html = await schedule(async () => {
          const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
          if (!response.ok) throw new Error('EBC request failed');
          return response.text();
        });
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.getElementById(`shopify-section-${this.dataset.sectionId}`);
        if (!section || section.querySelector('ebc-deferred')) throw new Error('Invalid EBC response');
        const dependencies = [...section.querySelectorAll('link[rel="stylesheet"], script[src]')];
        for (const node of dependencies) node.remove();
        const entries = [...section.querySelectorAll('.ebc-content__entry')];
        if (!entries.length) await loadDependencies(dependencies);
        // Shared EBC lists arrive in one request, but only nearby entries enter
        // the live DOM. Detached fragments are released after insertion.
        for (const entry of entries) {
          const placeholder = document.createElement('ebc-deferred');
          placeholder.style.cssText = 'display:block;min-height:500px';
          entry.replaceWith(placeholder);
          placeholder.dependencies = dependencies.filter(node => {
            const filename = new URL(node.src || node.href).pathname.split('/').pop();
            return filename === `${entry.dataset.ebcType}.css` || filename === `${entry.dataset.ebcType}.js`;
          });
          placeholder.fragment = document.createDocumentFragment();
          placeholder.fragment.append(entry);
        }
        if (!this.isConnected) return;
        const host = this.parentElement;
        this.replaceWith(...section.childNodes);
        // CX uses an imperative initializer; EBC custom elements self-initialize.
        window.CX?.initCoverflowCarousel?.(host);
      } catch (error) {
        if (!this.isConnected) return;
        this.loading = false;
        this.removeAttribute('aria-busy');
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = 'Load content';
        retry.addEventListener('click', () => { retry.remove(); this.load(); }, { once: true });
        this.replaceChildren(retry);
      } finally {
        this.loading = false;
      }
    }
  }
  customElements.define('ebc-deferred', EbcDeferred);
}
